import React, { useEffect } from "react";
import { Modal, Button, Form, Row, Col, InputGroup } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { cilTrash } from "@coreui/icons";
import CIcon from "@coreui/icons-react";

const documentOptions = [
  { value: "company_registration", label: "Company Registration (ABN/ACN)" },
  {
    value: "sled_certificate",
    label: "SLED – Scrap Trader Registration Certificate",
  },
  { value: "driver_license", label: "Driver's License" },
  {
    value: "address_proof",
    label:
      "Address Proof (Utility Bill, Bank Statement, Credit Card Statement)",
  },
  { value: "other", label: "Others" },
];

// Validation Schema
const validationSchema = Yup.object({
  name: Yup.string().required("Full Name is required"),
  documents: Yup.array()
    .min(2, "Two documents are required")
    .of(
      Yup.object().shape({
        type: Yup.string().required("Document type is required"),
        customType: Yup.string().when("type", {
          is: "other",
          then: (schema) => schema.required("Please specify the document type"),
          otherwise: (schema) => schema.notRequired(),
        }),
        file: Yup.mixed()
          .required("File is required")
          .test("fileSize", "File size too large (max 5MB)", (value) =>
            value ? value.size <= 5 * 1024 * 1024 : true,
          )
          .test("fileFormat", "Unsupported file type", (value) =>
            value
              ? ["image/jpeg", "image/png", "application/pdf"].includes(
                  value.type,
                )
              : true,
          ),
      }),
    ),
  // isConsent: Yup.boolean()
  //   .required("Consent is required")
  //   .oneOf([true], "You must agree to the consent"),
});

const KycUploadModal = ({ open, onClose, onSave }) => {
  const formik = useFormik({
    initialValues: {
      kycType: "individual",
      name: "",
      documents: [
        { type: "", customType: "", file: null },
        { type: "", customType: "", file: null },
      ],
      // isConsent: false,
    },
    validationSchema,
    onSubmit: (values) => {
      onSave({
        name: values.name,
        kycType: values.kycType,
        documents: values.documents.map((doc) => {
          if (doc.type === "other") {
            return {
              type: doc.customType, // custom text typed by user
              file: doc.file,
            };
          }

          const selectedOption = documentOptions.find(
            (opt) => opt.value === doc.type,
          );

          return {
            type: selectedOption ? selectedOption.label : doc.type,
            file: doc.file,
          };
        }),

        // isConsent: values.isConsent,
      });
      onClose();
    },
  });

  useEffect(() => {
    if (!open) {
      // Reset form when modal closes
      formik.resetForm({
        values: {
          kycType: "individual",
          name: "",
          documents: [
            { type: "", customType: "", file: null },
            { type: "", customType: "", file: null },
          ],
          // isConsent: false,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const updateDocument = (index, field, value) => {
    const updatedDocs = [...formik.values.documents];
    updatedDocs[index][field] = value;
    if (field === "type" && value !== "other") {
      updatedDocs[index].customType = "";
    }
    formik.setFieldValue("documents", updatedDocs);
  };

  return (
    <Modal show={open} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <h5 className="mb-1 fw-bold">Profile Verification</h5>
          <small className="text-muted">
            Please provide your details and upload two identification documents.
          </small>
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={formik.handleSubmit}>
        <Modal.Body
          style={{ padding: "1.5rem", maxHeight: "60vh", overflowY: "auto" }}
        >
          <div className="d-flex flex-column gap-3">
            {/* KYC Type and Full Name - Compact Grid */}
            <Row>
              <Col xs={12} sm={6}>
                <Form.Group>
                  <Form.Label>KYC Type</Form.Label>
                  <Form.Control
                    type="text"
                    value="Individual"
                    disabled
                    style={{
                      backgroundColor: "#f5f5f5",
                      fontWeight: 500,
                      color: "rgba(0, 0, 0, 0.6)",
                    }}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} sm={6}>
                <Form.Group>
                  <Form.Label>Full Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    placeholder="Enter full name"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={formik.touched.name && !!formik.errors.name}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.name}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            {/* Document Upload Sections */}
            {formik.values.documents.map((doc, index) => (
              <div
                key={index}
                style={{
                  padding: "1rem",
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  backgroundColor: "#fafafa",
                }}
              >
                <h6 className="fw-bold mb-3">Document {index + 1}</h6>
                <Row className="g-2">
                  {/* Document Type - Full Width */}
                  <Col xs={12}>
                    <Form.Group>
                      <Form.Label>Document Type</Form.Label>
                      <Form.Select
                        value={doc.type}
                        onChange={(e) =>
                          updateDocument(index, "type", e.target.value)
                        }
                        isInvalid={
                          formik.touched.documents?.[index]?.type &&
                          !!formik.errors.documents?.[index]?.type
                        }
                      >
                        <option value="">Select Document Type</option>
                        {documentOptions
                          .filter((opt) => {
                            // Show all options for current document
                            // Hide options already selected in other documents
                            const selectedTypes = formik.values.documents
                              .map((d, i) => (i !== index ? d.type : null))
                              .filter(Boolean);
                            return !selectedTypes.includes(opt.value);
                          })
                          .map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                      </Form.Select>
                      {formik.touched.documents?.[index]?.type &&
                        formik.errors.documents?.[index]?.type && (
                          <Form.Control.Feedback
                            type="invalid"
                            style={{ display: "block" }}
                          >
                            {formik.errors.documents[index].type}
                          </Form.Control.Feedback>
                        )}
                    </Form.Group>
                  </Col>

                  {/* Custom Document Type - Full Width */}
                  {doc.type === "other" && (
                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label>Specify Document Type</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter document type"
                          value={doc.customType}
                          onChange={(e) =>
                            updateDocument(index, "customType", e.target.value)
                          }
                          isInvalid={
                            formik.touched.documents?.[index]?.customType &&
                            !!formik.errors.documents?.[index]?.customType
                          }
                        />
                        <Form.Control.Feedback type="invalid">
                          {formik.errors.documents?.[index]?.customType}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  )}

                  {/* File Upload - Full Width with Upload button on LEFT */}
                  <Col xs={12}>
                    <Form.Group>
                      <Form.Label>Upload Document</Form.Label>
                      <InputGroup>
                        <Button
                          variant="warning"
                          as="label"
                          htmlFor={`file-upload-${index}`}
                          style={{
                            backgroundColor: "#FBC02D",
                            borderColor: "#FBC02D",
                            color: "#000",
                            fontWeight: 600,
                            cursor: "pointer",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#F9A825";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "#FBC02D";
                          }}
                        >
                          {doc.file ? "Change" : "Upload"}
                          <input
                            id={`file-upload-${index}`}
                            type="file"
                            hidden
                            accept="image/*,application/pdf"
                            onChange={(e) =>
                              updateDocument(index, "file", e.target.files[0])
                            }
                          />
                        </Button>
                        <Form.Control
                          type="text"
                          placeholder="No file selected"
                          value={doc.file ? doc.file.name : ""}
                          readOnly
                          isInvalid={
                            formik.touched.documents?.[index]?.file &&
                            !!formik.errors.documents?.[index]?.file
                          }
                        />
                        {doc.file && (
                          <Button
                            variant="outline-danger"
                            onClick={() => updateDocument(index, "file", null)}
                            style={{
                              borderColor: "#dc3545",
                            }}
                          >
                            <CIcon icon={cilTrash} size="sm" />
                          </Button>
                        )}
                        {formik.touched.documents?.[index]?.file &&
                          formik.errors.documents?.[index]?.file && (
                            <Form.Control.Feedback
                              type="invalid"
                              style={{ display: "block" }}
                            >
                              {formik.errors.documents[index].file}
                            </Form.Control.Feedback>
                          )}
                      </InputGroup>
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            ))}

            {/* Highlighted Consent Section */}
            {/* <div
              style={{
                padding: "1rem",
                borderRadius: "8px",
                backgroundColor: "#fffde7",
                border: "1px solid #fff59d",
                boxShadow: "0 2px 4px rgba(251, 192, 45, 0.1)",
              }}
            >
              <Form.Group>
                <Form.Check
                  type="checkbox"
                  id="consent-checkbox"
                  name="isConsent"
                  checked={formik.values.isConsent}
                  onChange={formik.handleChange}
                  label={
                    <span style={{ fontWeight: 500, fontSize: "0.95rem" }}>
                      I confirm that I am authorized to handle the transactions
                      - sale, purchase, financial transaction etc.
                    </span>
                  }
                  isInvalid={
                    formik.touched.isConsent && !!formik.errors.isConsent
                  }
                  style={{
                    accentColor: "#FBC02D",
                  }}
                />
                {formik.touched.isConsent && formik.errors.isConsent && (
                  <div
                    style={{
                      color: "#dc3545",
                      fontSize: "0.875rem",
                      marginTop: "0.25rem",
                      marginLeft: "1.5rem",
                    }}
                  >
                    {formik.errors.isConsent}
                  </div>
                )}
              </Form.Group>
            </div> */}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => {
              formik.resetForm();
              onClose();
            }}
            style={{
              borderColor: "#e0e0e0",
              fontWeight: 500,
              borderRadius: "8px",
              padding: "0.5rem 1.5rem",
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="warning"
            disabled={!formik.isValid || !formik.dirty || formik.isSubmitting}
            style={{
              backgroundColor: "#FBC02D",
              borderColor: "#FBC02D",
              color: "#000",
              fontWeight: 600,
              borderRadius: "8px",
              padding: "0.5rem 2rem",
            }}
            onMouseEnter={(e) => {
              if (!e.currentTarget.disabled) {
                e.currentTarget.style.backgroundColor = "#F9A825";
              }
            }}
            onMouseLeave={(e) => {
              if (!e.currentTarget.disabled) {
                e.currentTarget.style.backgroundColor = "#FBC02D";
              }
            }}
          >
            {formik.isSubmitting ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Form>

      <style>{`
        .modal-header .btn-close {
          opacity: 0.5;
        }
        .modal-header .btn-close:hover {
          opacity: 1;
        }
        // .form-check-input:checked {
        //   background-color: #FBC02D;
        //   border-color: #FBC02D;
        // }
        // .form-check-input:focus {
        //   border-color: #FBC02D;
        //   box-shadow: 0 0 0 0.25rem rgba(251, 192, 45, 0.25);
        // }
        .modal-title small {
          display: block;
          font-size: 0.875rem;
          font-weight: 400;
          margin-top: 0.25rem;
        }
      `}</style>
    </Modal>
  );
};

export default KycUploadModal;
